/**
 * The console's one way to hand a person a CSV file (rd15 A4).
 *
 * WHY THIS EXISTS. Two pages now export what they show — the Books page (its
 * journal) and Activity (its filtered list) — and a second copy of the download
 * helper would be a second place for a quoting rule to drift. `csvField` was
 * already shared for the same reason (a downloaded file must never open as a
 * live spreadsheet formula); this module is the file half of that rule.
 *
 * Nothing here reads or writes anything: the caller builds the rows.
 */

import { csvField } from "../csv_field.js";

/** One CSV line. Every cell goes through the shared injection guard. */
export function csvLine(cells: readonly string[]): string {
  return cells.map((cell) => csvField(cell)).join(",");
}

/** A whole file: a header line, then one line per row. Ends with a newline. */
export function csvText(
  header: readonly string[],
  rows: readonly (readonly string[])[],
): string {
  const lines = [csvLine(header), ...rows.map((row) => csvLine(row))];
  return `${lines.join("\n")}\n`;
}

/**
 * Save a CSV the browser downloads. A blob URL, a click, then the URL is
 * released: nothing is left behind, and no server is asked for anything.
 */
export function saveCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.rel = "noopener";
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
